import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Star, Quote, Gift } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

const pick = (project, index) => project?.media?.[index]?.url || project?.cover_image;
const FAMILY_WORDS = /maternity|newborn|gravidanza|neonat|premaman|mamma/i;

function FamilyReview() {
  const [review, setReview] = useState(null);
  useEffect(() => {
    api.get('/public/reviews')
      .then((res) => setReview(res.data.find((r) => r.rating >= 5 && FAMILY_WORDS.test(`${r.event_service || ''} ${r.text || ''}`)) || null))
      .catch(() => setReview(null));
  }, []);
  if (!review) return null;
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mt-16 lg:mt-24 max-w-4xl mx-auto text-center border-t border-[#2b2521]/10 pt-12"
      data-testid="family-review"
    >
      <Quote size={28} className="mx-auto text-[#8a6f5f] mb-4" />
      <div className="flex justify-center gap-1 mb-5" aria-label={`${review.rating} stelle`}>
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} className="fill-[#c9a27a] text-[#c9a27a]" />)}
      </div>
      <blockquote className="font-heading text-xl md:text-2xl leading-relaxed text-[#2b2521]" data-testid="family-review-text">
        “{review.text}”
      </blockquote>
      <figcaption className="mt-6 text-sm text-[#5b4f48]" data-testid="family-review-author">
        <span className="font-medium text-[#2b2521]">{review.name}</span>
        {review.event_service && <span> · {review.event_service}</span>}
        {review.source && <span className="text-[#8a6f5f]"> · via {review.source}</span>}
      </figcaption>
      <Link to="/recensioni" className="inline-flex items-center gap-1 mt-5 text-sm text-[#8a6f5f] hover:text-[#2b2521] transition-colors" data-testid="family-review-all-link">
        Leggi tutte le recensioni <ArrowRight size={14} />
      </Link>
    </motion.figure>
  );
}

export default function FamilyMomentsSection({ projects }) {
  const newborn = projects.find((p) => p.slug === 'newborn');
  const maternity = projects.find((p) => p.slug === 'maternity');
  if (!newborn && !maternity) return null;

  return (
    <section className="py-24 lg:py-32 px-6 bg-[#f6f1ec] text-[#2b2521]" data-testid="family-moments-section">
      <div className="max-w-[1800px] mx-auto">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 order-2 lg:order-1"
        >
          <span className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-[#8a6f5f]">
            <Heart size={14} /> Maternity &amp; Newborn
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            I primi momenti,<br />per sempre.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#5b4f48] max-w-md">
            Dall'attesa ai primi giorni di vita: servizi dedicati alle future e neo mamme, con tempi lenti,
            luce naturale e tutta la delicatezza che questi momenti meritano. In studio o al tramonto, sul mare.
          </p>
          <p className="mt-4 text-sm text-[#8a6f5f]">
            Pacchetto <strong className="text-[#2b2521]">Maternity + Newborn</strong> da € 650 · 2 sessioni · 40+ foto editate
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/prenota?servizio=Maternity%20%2B%20Newborn" data-testid="family-book-btn">
              <Button size="lg" className="bg-[#2b2521] text-[#f6f1ec] hover:bg-[#5b4f48]">
                Prenota il pacchetto Maternity + Newborn
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            {maternity && (
              <Link to={`/portfolio/${maternity.slug}`} data-testid="family-maternity-link">
                <Button size="lg" variant="outline" className="border-[#2b2521] text-[#2b2521] hover:bg-[#2b2521] hover:text-[#f6f1ec]">
                  Maternity
                </Button>
              </Link>
            )}
            {newborn && (
              <Link to={`/portfolio/${newborn.slug}`} data-testid="family-newborn-link">
                <Button size="lg" variant="outline" className="border-[#2b2521] text-[#2b2521] hover:bg-[#2b2521] hover:text-[#f6f1ec]">
                  Newborn
                </Button>
              </Link>
            )}
          </div>
          <Link to="/buono-regalo" className="inline-flex items-center gap-2 mt-6 text-sm text-[#8a6f5f] hover:text-[#2b2521] transition-colors" data-testid="family-gift-link">
            <Gift size={16} /> Vuoi regalarlo a una futura mamma? Scopri il Buono Regalo <ArrowRight size={14} />
          </Link>
        </motion.div>

        <div className="lg:col-span-7 order-1 lg:order-2 grid grid-cols-12 gap-4 lg:gap-6">
          {maternity && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="col-span-7 lg:mt-16"
            >
              <Link to={`/portfolio/${maternity.slug}`} className="group block" data-testid="family-maternity-card">
                <div className="aspect-[3/4] overflow-hidden bg-[#e9dfd6]">
                  <img src={pick(maternity, 0)} alt="Servizio fotografico maternity al tramonto" className="w-full h-full object-cover img-zoom" loading="lazy" />
                </div>
                <p className="mt-3 font-heading text-xl">Maternity</p>
                <p className="text-sm text-[#8a6f5f]">L'attesa, raccontata con dolcezza</p>
              </Link>
            </motion.div>
          )}
          {newborn && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="col-span-5"
            >
              <Link to={`/portfolio/${newborn.slug}`} className="group block" data-testid="family-newborn-card">
                <div className="aspect-[4/5] overflow-hidden bg-[#e9dfd6]">
                  <img src={pick(newborn, 11)} alt="Servizio fotografico newborn in studio" className="w-full h-full object-cover img-zoom" loading="lazy" />
                </div>
                <div className="aspect-square overflow-hidden bg-[#e9dfd6] mt-4 lg:mt-6 hidden sm:block">
                  <img src={pick(newborn, 18)} alt="Neonato con i genitori" className="w-full h-full object-cover img-zoom" loading="lazy" />
                </div>
                <p className="mt-3 font-heading text-xl">Newborn</p>
                <p className="text-sm text-[#8a6f5f]">I primi giorni di vita, in studio</p>
              </Link>
            </motion.div>
          )}
        </div>
      </div>
      <FamilyReview />
      </div>
    </section>
  );
}
